import * as THREE from 'three';
import type { MaterialParams, ProfileId } from '../services/destructionProfiles';
import { DESTRUCTION_PROFILES } from '../services/destructionProfiles';
import { createSurfaceMaterial } from './core/SurfaceMaterial';
import { cloneBakedDamageMaterial, isBakedDamageMaterial } from './core/BakedSurfaceMaterial';
import {
  NEUTRAL_APPEARANCE_DESCRIPTOR,
  type MaterialAppearanceDescriptor,
} from './appearanceDescriptor';
import type { SurfaceBundle, SurfaceDefinition, SurfaceFace } from './core/SurfaceDefinition';
import { SURFACE_DEFINITION as concreteSurface } from './authoring/ConcreteMaterial';
import { SURFACE_DEFINITION as brickSurface } from './authoring/BrickMaterial';
import { SURFACE_DEFINITION as stoneSurface } from './authoring/StoneMaterial';
import { SURFACE_DEFINITION as woodSurface } from './authoring/WoodMaterial';
import { SURFACE_DEFINITION as plasterSurface } from './authoring/PlasterMaterial';
import type { ConcreteMaterialSettings } from './authoring/ConcreteMaterial';
import type { StoneMaterialSettings } from './authoring/StoneMaterial';
import type { BrickMaterialSettings } from './authoring/BrickMaterial';
import type { WoodMaterialSettings } from './authoring/WoodMaterial';
import type { PlasterMaterialSettings } from './authoring/PlasterMaterial';

export type { MaterialAppearanceDescriptor } from './appearanceDescriptor';
export { NEUTRAL_APPEARANCE_DESCRIPTOR } from './appearanceDescriptor';
export type { SurfaceFace, SurfaceDefinition, SurfaceBundle } from './core/SurfaceDefinition';

const SURFACES = new Map<ProfileId, SurfaceDefinition>();

export function registerSurface(def: SurfaceDefinition): void {
  SURFACES.set(def.id, def);
}

export function getSurface(id: ProfileId): SurfaceDefinition | undefined {
  return SURFACES.get(id);
}

/** Registered look-dev surfaces. Glass/neutral are not registered. */
export function listSurfaces(): SurfaceDefinition[] {
  return [...SURFACES.values()];
}

export function getSurfaceBundle(id: ProfileId): SurfaceBundle | null {
  const definition = SURFACES.get(id);
  if (!definition) return null;
  return {
    definition,
    physics: definition.physics,
    appearance: definition.appearance,
  };
}

registerSurface(concreteSurface);
registerSurface(brickSurface);
registerSurface(stoneSurface);
registerSurface(woodSurface);
registerSurface(plasterSurface);

/** Presentation-safe material look. Glass and unknown ids use the neutral fallback. */
export function getAppearanceDescriptor(profileId: ProfileId): MaterialAppearanceDescriptor {
  return SURFACES.get(profileId)?.appearance ?? NEUTRAL_APPEARANCE_DESCRIPTOR;
}

export interface ProfileMaterialSettings {
  concrete: ConcreteMaterialSettings;
  stone?: StoneMaterialSettings;
  brick?: BrickMaterialSettings;
  wood?: WoodMaterialSettings;
  plaster?: PlasterMaterialSettings;
}

function settingsFor(
  profileId: ProfileId,
  def: SurfaceDefinition,
  settings: ProfileMaterialSettings,
): unknown {
  if (profileId === 'concrete') return settings.concrete ?? def.defaultSettings;
  if (profileId === 'stone') return settings.stone ?? def.defaultSettings;
  if (profileId === 'brick') return settings.brick ?? def.defaultSettings;
  if (profileId === 'wood') return settings.wood ?? def.defaultSettings;
  if (profileId === 'plaster') return settings.plaster ?? def.defaultSettings;
  return def.defaultSettings;
}

/**
 * Single extension point for profile-to-renderer wiring. New materials add a
 * definition via registerSurface while the demolition scene remains material-agnostic.
 */
export function createProfileMaterial(
  profileId: ProfileId,
  params: MaterialParams,
  face: SurfaceFace,
  settings: ProfileMaterialSettings,
): THREE.MeshStandardMaterial {
  const def = SURFACES.get(profileId);
  if (!def) {
    return createSurfaceMaterial(params, {
      roughness: params.roughness,
      metalness: params.metalness ?? 0,
      shaderOwnsAlbedo: false,
    });
  }
  return def.createMaterial(params, settingsFor(profileId, def, settings), face);
}

export function updateProfileMaterial(
  profileId: ProfileId,
  material: THREE.Material,
  settings: ProfileMaterialSettings,
): void {
  const def = SURFACES.get(profileId);
  if (!def) return;
  def.updateMaterial(material, settingsFor(profileId, def, settings));
}

/** Clone custom profile materials with their shader state intact for fade-outs. */
export function cloneProfileMaterial(material: THREE.Material): THREE.Material {
  if (isBakedDamageMaterial(material)) return cloneBakedDamageMaterial(material);
  for (const def of SURFACES.values()) {
    if (def.isMaterial(material)) return def.cloneMaterial(material);
  }
  return material.clone();
}

export function assertSurfacePhysicsLinked(): boolean {
  for (const def of SURFACES.values()) {
    if (def.physics !== DESTRUCTION_PROFILES[def.id]) return false;
  }
  return true;
}
